import { DevTool } from '@hookform/devtools';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from '@tanstack/react-router';
import { ConditionType } from '@uturn/api/v1';
import { Permission } from '@uturn/portal/abac';
import InsuredLimitThresholdBanner from '@uturn/portal/components/insured-limit-threshold-banner';
import { Page } from '@uturn/portal/components/page';
import { MetaDataContext } from '@uturn/portal/context';
import {
	PublishDetailsSection,
	RouteSection,
	ShipmentDetailsSection,
	ShipmentMapSection,
} from '@uturn/portal/modules/shipments/components';
import type { FormValues } from '@uturn/portal/modules/shipments/schema';
import { formSchema } from '@uturn/portal/modules/shipments/schema';
import {
	fromForm,
	fromZodLocations,
	shouldUpdate,
	typeOfShipmentName,
} from '@uturn/portal/modules/shipments/utils';
import {
	useContainerNumberValidation,
	useGrossWeightValidation,
	useGroupedShipmentValidation,
	useLocationDatesValidation,
	usePriceValidation,
	useShipperReferenceValidation,
} from '@uturn/portal/modules/shipments/validation';
import { useAddShipmentStore } from '@uturn/portal/store/shipments/add-shipment';
import type { Documents } from '@uturn/portal/types/shipment';
import {
	ContainerStatus,
	initDocuments,
	TypeOfShipment,
} from '@uturn/portal/types/shipment';
import { getApiErrorMessage } from '@uturn/portal/utils';
import { scrollToFirstFormError } from '@uturn/portal/utils/scroll-to-first-form-error';
import { AlertDialog, AlertDialogContent, Form, sonner } from '@uturn/ui-kit';
import posthog from 'posthog-js';
import { useContext, useEffect, useRef, useState } from 'react';
import { useAbac } from 'react-abac';
import { Helmet } from 'react-helmet-async';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CreditLimitPublishBlockedModal } from '../../components/credit-limit-publish-blocked-modal';
import SummarySection from '../../components/summary-section';
import { getShipmentModalContent } from './shipment-modal-content';
import { SideBarActions } from './sidebar-actions';

export function ManualShipment() {
	const {
		initData,
		copyData,
		transportType,
		typeOfShipment,
		orgDefaultValues,
		createShipment,
	} = useAddShipmentStore();
	const { t } = useTranslation();
	const { userHasPermissions } = useAbac();
	const { validateLocationDates } = useLocationDatesValidation();
	const { validateGrossWeight } = useGrossWeightValidation();
	const { validateShipperReference } = useShipperReferenceValidation();
	const { validateGroupedShipment } = useGroupedShipmentValidation();
	const { validatePrice } = usePriceValidation();
	const { validateContainerNumber, validateContainerNumberDuplicate } =
		useContainerNumberValidation();

	const { shipmentMetaData } = useContext(MetaDataContext);
	const router = useRouter();

	const [sendingShipmentData, setSendingShipmentData] = useState(false);
	const [openSavedPublishedModal, setOpenSavedPublishedModal] = useState(false);
	const [shipmentNumber, setShipmentNumber] = useState('');
	const [newFiles, setNewFiles] = useState<Documents>(initDocuments);
	const [
		creditLimitPublishBlockedModalOpen,
		setCreditLimitPublishBlockedModalOpen,
	] = useState(false);

	const initDataLoaded = Object.keys(initData)?.length > 0;
	const copyDataLoaded = Object.keys(copyData)?.length > 0;
	const storeNotFound = transportType && !initDataLoaded && !copyDataLoaded;

	const form = useForm<Partial<FormValues>>({
		resolver: zodResolver(formSchema),
		defaultValues: copyDataLoaded ? copyData : initData,
		mode: 'onBlur',
	});

	// const [fieldFocus, setFieldFocus] = useState<keyof FormValues | undefined>(undefined);
	const [isCrossBorderShipment, setIsCrossBorderShipment] =
		useState<boolean>(false);
	const [isGBRShipment, setIsGBRShipment] = useState(false);

	const formRef = useRef<HTMLFormElement>(null);

	const watchIsConcept = form.watch('concept');

	/**
	 * // TODO: Revisit the setIsCrossBorderShipment logic because and FR - UK shipment
	 *          is also cross-border, right?
	 *
	 * Currently we check both the 2 letter notation aswell as the 3 letter notation
	 * The reason for this is that the shipment endpoint which loads the initial data returns
	 * the 2 letter notation, but the autocomplete returns the 3 letter notation
	 *
	 * This counts for both the hasCrossBorderCountry and isGBRShipment check
	 *
	 */
	const setApplicableConditions = (values: Partial<FormValues>) => {
		const { countryCodes } = fromZodLocations(values.locations ?? []);

		setIsCrossBorderShipment(
			countryCodes.length > 0 &&
				countryCodes.some((country) => country !== 'NLD' && country !== 'NL'),
		);
		setIsGBRShipment(
			countryCodes.length > 0 &&
				countryCodes.every((country) => country === 'GBR' || country === 'GB'),
		);
	};

	useEffect(() => {
		const subscription = form.watch((value, { name }) => {
			if (name?.match(/^locations\.[0-9]*\.countryCode/)) {
				setApplicableConditions(value);
			}
		});

		return () => subscription.unsubscribe();
	}, [form.watch]);

	useEffect(() => {
		// const { conditions: defaultConditions } = orgDefaultValues ?? {};
		switch (true) {
			// case defaultConditions:
			// 	form.setValue('conditions', defaultConditions, shouldUpdate);
			// 	break;
			case isGBRShipment:
				form.setValue('conditions', ConditionType.RHA, shouldUpdate);
				break;
			case isCrossBorderShipment:
				form.setValue('conditions', ConditionType.CMR, shouldUpdate);
				break;
			default:
				form.setValue('conditions', ConditionType.AVC, shouldUpdate);
				break;
		}
	}, [isCrossBorderShipment, isGBRShipment, orgDefaultValues]);

	// useEffect(() => {
	// 	form.setFocus(fieldFocus, { shouldSelect: true });
	// }, [form.setFocus, fieldFocus]);

	const handleFormSubmit = () => {
		if (formRef.current === null) {
			return;
		}

		form.clearErrors();

		formRef.current.dispatchEvent(
			new Event('submit', { cancelable: true, bubbles: true }),
		);
	};

	const handleConcept = () => {
		form.setValue('concept', true, shouldUpdate);

		posthog.capture('shipment_published', {
			name: 'button_save_concept',
			transport_type: transportType,
			shipment_type: typeOfShipment,
			group_name: form.getValues('newGroup.name') ?? '',
			number_of_shipments: form.getValues('numberOfCopiesToBeCreated') ?? 1,
			target_price: form.getValues('price.quantity') ?? 0,
		});

		handleFormSubmit();
	};

	const handlePublish = () => {
		form.setValue('concept', false, shouldUpdate);

		if (!userHasPermissions(Permission.PUBLISH_SHIPMENT)) {
			setOpenSavedPublishedModal(true);
			return;
		}

		posthog.capture('shipment_published', {
			name: 'button_publish',
			transport_type: transportType,
			shipment_type: typeOfShipment,
			group_name: form.getValues('newGroup.name') ?? '',
			number_of_shipments: form.getValues('numberOfCopiesToBeCreated') ?? 1,
			target_price: form.getValues('price.quantity') ?? 0,
		});

		handleFormSubmit();
	};

	const isFormValid = async (data: FormValues) => {
		form.clearErrors();

		const publishing = !(data.concept ?? true);

		let results = validateGroupedShipment(
			data.newGroup?.name ?? '',
			data.numberOfCopiesToBeCreated ?? 1,
			typeOfShipment,
		);

		results = validateGrossWeight(
			data.grossWeight?.quantity ?? 0,
			data.containerStatus ?? ContainerStatus.FULL,
			publishing,
			results,
		);

		results = validateLocationDates(
			data.locationActions,
			data.publishUntilDate ?? '',
			publishing,
			!publishing,
			results,
		);

		results = validatePrice(data.price?.quantity ?? 0, publishing, results);

		results = validateShipperReference(
			data.shipperReference ?? '',
			data.invoiceRequired ?? false,
			publishing,
			results,
		);

		results = validateContainerNumber(
			data.equipment?.containerNumber ?? '',
			data.shippingLine?.code,
			results,
		);

		if (!results.valid) {
			results.issues.forEach(({ path, message }) =>
				form.setError(path, { type: 'custom', message }),
			);
			form.setFocus(results.issues[0].path, { shouldSelect: true });
			scrollToFirstFormError();

			return false;
		}

		// Non-blocking: check for duplicate container number
		// (shows warning toast only, cause inline error was already shown, so no need to show it again)
		results = await validateContainerNumberDuplicate(
			data.equipment?.containerNumber ?? '',
			0,
		);

		if (!results.valid) {
			results.issues.forEach(({ message }) => {
				sonner.warning(message);
			});
		}

		return true;
	};

	const onSubmit = async (data: FormValues) => {
		const formValid = await isFormValid(data);
		if (!formValid) {
			return false;
		}

		const hasFiles =
			newFiles.fileRequests.length > 0 && newFiles.files.length > 0;
		const postData = {
			...fromForm(data),
			...(hasFiles ? { fileRequests: newFiles.fileRequests } : {}),
		};

		const formData = new FormData();

		if (hasFiles) {
			newFiles.files.forEach((file) => {
				formData.append('files', file);
			});
		}

		formData.append(
			'shipment',
			new Blob([JSON.stringify(postData)], {
				type: 'application/json',
			}),
		);

		setSendingShipmentData(true);
		const result = await createShipment(formData);
		setSendingShipmentData(false);

		if (result.status === 200) {
			const createdShipmentNumber = result.data[0] ?? '';
			setShipmentNumber(String(createdShipmentNumber));
			return setOpenSavedPublishedModal(true);
		}

		const message = getApiErrorMessage(result);

		if (message === 'CREDIT_LIMIT_EXCEEDED') {
			setCreditLimitPublishBlockedModalOpen(true);
			sonner.error(
				t(
					'pages.publish-shipment.api-error.credit-limit-exceeded',
					'Credit limit exceeded',
				),
			);
			return false;
		}

		sonner.error(
			message ??
				t(
					'pages.publish-shipment.api-error.generic',
					'Something went wrong while creating the shipment. Please try again.',
				),
		);
		return false;
	};

	// On page refresh, the store is reset. Need to go back.
	if (storeNotFound) {
		return router.navigate({ to: '/shipments/add' });
	}

	const title = (
		typeOfShipment === TypeOfShipment.GROUP
			? t(
					'pages.create_grouped_shipment.title',
					'Create identical {{transportType}} shipments',
					{ transportType: typeOfShipmentName(t)[typeOfShipment] },
				)
			: t('pages.create_shipment.title', 'Create {{transportType}} shipment', {
					transportType: typeOfShipmentName(t)[typeOfShipment],
				})
	).toString();

	return (
		<>
			<Helmet title={title} />
			<Page
				title={title}
				backButton={{
					label: 'Add new shipment',
					href: '/shipments/add',
				}}
			>
				<InsuredLimitThresholdBanner className="mt-4" />

				<div className="relative min-h-screen pb-16 grid grid-cols-[60%_auto] gap-2.5">
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit, scrollToFirstFormError)}
							ref={formRef}
						>
							<div className="flex flex-col gap-4">
								<ShipmentDetailsSection
									containerTypes={shipmentMetaData?.shipmentUnits ?? []}
									shipmentRequirements={
										shipmentMetaData?.shipmentRequirements ?? []
									}
									shippingLines={shipmentMetaData?.shippingLines ?? []}
									setNewFiles={(files, fileRequests) => {
										setNewFiles({
											files,
											fileRequests,
										});
									}}
									enableAddingGroup={typeOfShipment === TypeOfShipment.GROUP}
								/>
								<RouteSection transportType={transportType ?? ''} />
								<PublishDetailsSection
									isCrossBorderShipment={isCrossBorderShipment}
									isGBRShipment={isGBRShipment}
								/>
							</div>
						</form>
						{import.meta.env.DEV && (
							<DevTool control={form.control} placement="top-right" />
						)}
						<div className="sticky top-16 h-[50vh] print:hidden">
							<ShipmentMapSection />
							<SummarySection transportType={transportType} isShipmentCreation>
								{({ totalPrice }) => (
									<SideBarActions
										sendingShipmentData={sendingShipmentData}
										publishShipment={handlePublish}
										saveAsConcept={handleConcept}
										isConcept={watchIsConcept ?? true}
										isGroupedShipment={typeOfShipment === TypeOfShipment.GROUP}
										numberOfShipments={form.watch('numberOfCopiesToBeCreated')}
										isDirty={form.formState.isDirty}
										totalPrice={totalPrice ?? undefined}
										onPublishClick={() =>
											setCreditLimitPublishBlockedModalOpen(true)
										}
									/>
								)}
							</SummarySection>
						</div>
					</Form>
				</div>
				<AlertDialog open={openSavedPublishedModal}>
					<AlertDialogContent className="max-w-[25rem] md:max-w-[40rem]">
						{getShipmentModalContent(
							watchIsConcept ?? true,
							typeOfShipment,
							shipmentNumber,
							router.navigate,
							() => setOpenSavedPublishedModal(false),
							form.getValues('numberOfCopiesToBeCreated') ?? 0,
							form.getValues('newGroup.name'),
						)}
					</AlertDialogContent>
				</AlertDialog>

				<CreditLimitPublishBlockedModal
					open={creditLimitPublishBlockedModalOpen}
					onSubmit={() => {
						setCreditLimitPublishBlockedModalOpen(false);
						handleConcept();
					}}
					isSubmitting={sendingShipmentData}
					onCancel={() => {
						setCreditLimitPublishBlockedModalOpen(false);
					}}
				/>
			</Page>
		</>
	);
}
